import { createOpenAIProvider } from './openai';
import { createGeminiProvider } from './gemini';
import type { OpenAI } from '@ai-sdk/openai';
import type { GoogleGenerativeAI } from '@ai-sdk/google';

/**
 * Supported AI provider types
 */
export type ProviderType = 'openai' | 'gemini';

/**
 * Provider instance type (union of all provider types)
 */
export type AIProvider = OpenAI | GoogleGenerativeAI;

/**
 * Get API key for a specific provider
 */
export function getProviderApiKey(type: ProviderType): string | null {
  switch (type) {
    case 'openai':
      return localStorage.getItem('openai_api_key');
    case 'gemini':
      return localStorage.getItem('gemini_api_key');
    default:
      return null;
  }
}

/**
 * Check if a provider has an API key configured
 */
export function hasProviderApiKey(type: ProviderType): boolean {
  const key = getProviderApiKey(type);
  return key !== null && key.trim().length > 0;
}

/**
 * Create an AI provider instance for the specified provider type
 */
export function createAIProvider(type: ProviderType): AIProvider {
  switch (type) {
    case 'openai':
      return createOpenAIProvider();
    case 'gemini':
      return createGeminiProvider();
    default:
      throw new Error(`Unsupported provider type: ${type}`);
  }
}

/**
 * Get the default provider preference from localStorage
 * Falls back to 'gemini' if not set or if preferred provider has no key
 */
export function getDefaultProvider(): ProviderType {
  const preference = localStorage.getItem('ai_provider_preference') as ProviderType | null;
  
  // If preference is set and has API key, use it
  if (preference && hasProviderApiKey(preference)) {
    return preference;
  }
  
  // Otherwise, check which providers are available
  // Prefer Gemini as default
  if (hasProviderApiKey('gemini')) {
    return 'gemini';
  }
  
  if (hasProviderApiKey('openai')) {
    return 'openai';
  }
  
  // Default to Gemini (will throw error if no key is set)
  return 'gemini';
}

/**
 * Set the default provider preference
 */
export function setDefaultProvider(type: ProviderType): void {
  localStorage.setItem('ai_provider_preference', type);
}

/**
 * Get available providers (those with API keys configured)
 */
export function getAvailableProviders(): ProviderType[] {
  const providers: ProviderType[] = [];
  
  if (hasProviderApiKey('openai')) {
    providers.push('openai');
  }
  
  if (hasProviderApiKey('gemini')) {
    providers.push('gemini');
  }
  
  return providers;
}

